/**
 * Gun skins (ADR-0095): a finish every gun you hold is painted in, seen by everyone in the match. Each recolours the
 * guns' art (assets' `GUN_SKIN_PALETTES` has the palettes, keyed like this). `stock` is the art as drawn and everyone's;
 * the rest are sold in the Store (`PRICES.gun`). A skin on the wire is its index here: append, never insert.
 * An `effect` skin (ADR-0113) is no finish: it redraws the default gun alone, with its bullets, flash, hits and sounds
 * (the client's `SKIN_FX`); every other gun stays stock in it. A `polished` skin (ADR-0148) redraws the default gun
 * smooth, not in pixels (assets' `POLISHED`), its owner's kills counted on it; its shots stay stock, every other gun is
 * painted in its finish: sold in the Store, the dearest skins.
 * A `preview` skin (ADR-0154) is in the build but nobody's: the Store doesn't sell it until an admin releases it at a
 * price (the admin app's Skins page), and stops when they pull it (whoever bought it keeps it). Removing one that is the
 * last of the list and nobody's frees its index for the next (the four first in preview went so, protocol 81).
 */
export const GUN_SKINS = {
  stock: { name: 'Stock' },
  ivory: { name: 'Ivory' },
  ember: { name: 'Ember' },
  frost: { name: 'Frost' },
  bubblegum: { name: 'Bubblegum' },
  royal: { name: 'Royal' },
  midnight: { name: 'Midnight' },
  gold: { name: 'Gold' },
  // The sets' finishes (ADR-0112), keyed like their set.
  gnome: { name: 'Toadstool' },
  beekeeper: { name: 'Honey' },
  astronaut: { name: 'Cosmos' },
  pumpkin: { name: 'Pumpkin' },
  // Effect skins (ADR-0113).
  bubble: { name: 'Bubble gun', effect: true },
  angel: { name: 'Angel wings', effect: true },
  plasma: { name: 'Plasma gun', effect: true },
  hose: { name: 'Garden hose', effect: true },
  // A season's finish (ADR-0123), keyed like its set: only the pass gives it.
  reaper: { name: 'Bone' },
  // A verified creator's finish (ADR-0128): only an admin's approval gives it (content `CREATOR_ITEMS`).
  creator: { name: 'Creator' },
  // Polished skins (ADR-0148): sold for 25,000 seeds (the legends after them 65,000).
  polished_chrome: { name: 'Mirror Chrome', polished: true },
  polished_gold: { name: '24K Gold', polished: true },
  polished_rose: { name: 'Rose Gold', polished: true },
  polished_copper: { name: 'Copper', polished: true },
  polished_platinum: { name: 'Platinum', polished: true },
  polished_onyx: { name: 'Onyx', polished: true },
  polished_cobalt: { name: 'Cobalt', polished: true },
  polished_amethyst: { name: 'Amethyst', polished: true },
  polished_candy: { name: 'Candy Apple', polished: true },
  polished_pearl: { name: 'Pearl', polished: true },
  polished_glacier: { name: 'Glacier', polished: true },
  polished_heat: { name: 'Heat Tint', polished: true },
  polished_prism: { name: 'Prism', polished: true },
  polished_damascus: { name: 'Damascus', polished: true },
  polished_engraved: { name: 'Engraved', polished: true },
  polished_twotone: { name: 'Two-Tone', polished: true },
  polished_ivory: { name: 'Ivory & Gold', polished: true },
  polished_starlight: { name: 'Starlight', polished: true },
  polished_bubblegum: { name: 'Bubblegum', polished: true },
  polished_tiger: { name: 'Tiger', polished: true },
  // The legends (ADR-0148 amended): the dearest finishes, each a picture of its own.
  polished_dragonlore: { name: 'Dragon Bloom', polished: true },
  polished_bluegem: { name: 'Blue Gem', polished: true },
  polished_howl: { name: 'Wolfsbane', polished: true },
  polished_serpent: { name: 'Fire Viper', polished: true },
  polished_lotus: { name: 'Night Lotus', polished: true },
  polished_gungnir: { name: 'Thunder Spear', polished: true },
  polished_ruby: { name: 'Ruby', polished: true },
  polished_sapphire: { name: 'Sapphire', polished: true },
  polished_blackpearl: { name: 'Black Pearl', polished: true },
  polished_emerald: { name: 'Emerald', polished: true },
  polished_fade: { name: 'Fade', polished: true },
  polished_marblefade: { name: 'Candy Swirl', polished: true },
  polished_crimsonweb: { name: 'Spider Lily', polished: true },
  // The mythic sets' guns (ADR-0160), keyed like their set: effect skins whose gun burns, crackles or turns on its own
  // (its set's `flair`, assets' `SET_ART`). Those in preview (ADR-0154) an admin releases at a price.
  inferno: { name: 'Hellfire', effect: true },
  kitsune: { name: 'Foxfire', effect: true },
  voidwalker: { name: 'Singularity', effect: true },
  tempest: { name: 'Thunderclap', effect: true },
  glacier: { name: 'Permafrost', effect: true },
  angler: { name: 'Tidecaller', effect: true },
  jester: { name: 'Jack-in-the-Box', effect: true },
  plague: { name: 'Censer', effect: true },
  chronomancer: { name: 'Tick-Tock', effect: true },
  glitch: { name: 'Overclock', effect: true },
  disco: { name: 'Boogie Blaster', effect: true },
  origami: { name: 'Foldfire', effect: true },
  ooze: { name: 'Gloop Gun', effect: true },
  barnstormer: { name: 'Sky Ace', effect: true },
  pirate: { name: 'Blunderbuss', effect: true, preview: true },
  magician: { name: 'Abracadabra', effect: true },
  chef: { name: 'Pepper Mill', effect: true, preview: true },
  knight: { name: 'Arbalest', effect: true, preview: true },
  sheriff: { name: 'Peacemaker', effect: true, preview: true },
  luchador: { name: 'Main Event', effect: true, preview: true },
} as const satisfies Record<string, { name: string; effect?: true; polished?: true; preview?: true }>;

export type GunSkin = keyof typeof GUN_SKINS;
/** A skin that redraws the default gun (ADR-0113). */
export type EffectSkin = { [K in GunSkin]: (typeof GUN_SKINS)[K] extends { effect: true } ? K : never }[GunSkin];
/** A skin that redraws the default gun smooth (ADR-0148). */
export type PolishedSkin = { [K in GunSkin]: (typeof GUN_SKINS)[K] extends { polished: true } ? K : never }[GunSkin];

/** Every skin, in the Store's order; a skin on the wire is its index here. */
export const GUN_SKIN_KEYS = Object.keys(GUN_SKINS) as GunSkin[];

export const isGunSkin = (key: string): key is GunSkin => Object.hasOwn(GUN_SKINS, key);

/** Is `key` an effect skin (ADR-0113)? */
export const isEffectSkin = (key: string): key is EffectSkin => isGunSkin(key) && 'effect' in GUN_SKINS[key];

/** Is `key` a polished skin (ADR-0148)? */
export const isPolishedSkin = (key: string): key is PolishedSkin => isGunSkin(key) && 'polished' in GUN_SKINS[key];

/** Every polished skin, in the Store's order. */
export const POLISHED_SKINS = GUN_SKIN_KEYS.filter(isPolishedSkin);

/** Is `key` a skin in preview (ADR-0154): sold only once an admin releases it? */
export const isPreviewSkin = (key: string): boolean => isGunSkin(key) && 'preview' in GUN_SKINS[key];

/** Every skin in preview, in the Store's order. */
export const PREVIEW_SKINS = GUN_SKIN_KEYS.filter(isPreviewSkin);
